import React from 'react';
import styled from 'styled-components';
import { FaUtensils, FaClock, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaStar, FaCheck } from 'react-icons/fa';
import DemoViewer from '../../components/DemoViewer';
import SEO from '../../components/SEO';

// Estilos para la demo de restaurante
const RestaurantDemo = styled.div`
  font-family: 'Montserrat', sans-serif;
  color: #333;
`;

const HeroSection = styled.div`
  background-image: url('/servicios/tipo-cliente/restaurantes-hero.webp');
  background-size: cover;
  background-position: center;
  height: 550px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
`;

const Logo = styled.div`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  letter-spacing: 2px;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ReservationButton = styled.button`
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #c0392b;
    transform: translateY(-2px);
  }
`;

const Section = styled.section`
  padding: 5rem 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &::after {
    content: '';
    width: 80px;
    height: 3px;
    background-color: #e74c3c;
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const MenuSection = styled(Section)`
  background-color: #f9f9f9;
`;

const MenuCategories = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const CategoryButton = styled.button<{ active?: boolean }>`
  background-color: ${props => props.active ? '#e74c3c' : 'transparent'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 1px solid ${props => props.active ? '#e74c3c' : '#ddd'};
  padding: 0.75rem 1.5rem;
  margin: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 30px;
  
  &:hover {
    background-color: ${props => props.active ? '#c0392b' : '#f1f1f1'};
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const MenuItem = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const MenuItemImage = styled.div`
  height: 200px;
  background-color: #eee;
  background-size: cover;
  background-position: center;
`;

const MenuItemContent = styled.div`
  padding: 1.5rem;
`;

const MenuItemTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
`;

const MenuItemPrice = styled.span`
  color: #e74c3c;
  font-weight: bold;
`;

const MenuItemDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const AboutSection = styled(Section)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const AboutContent = styled.div`
  flex: 1;
  min-width: 300px;
`;

const AboutTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  position: relative;
  
  &::after {
    content: '';
    width: 80px;
    height: 3px;
    background-color: #e74c3c;
    position: absolute;
    bottom: -1rem;
    left: 0;
  }
`;

const AboutText = styled.p`
  color: #666;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
`;

const AboutImage = styled.div`
  flex: 1;
  min-width: 300px;
  height: 400px;
  background-image: url('/servicios/tipo-cliente/restaurante-interior.webp');
  background-size: cover;
  background-position: center;
  border-radius: 8px;
`;

const InfoSection = styled.div`
  display: flex;
  margin-top: 3rem;
  flex-wrap: wrap;
  gap: 2rem;
`;

const InfoItem = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: flex-start;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
  color: #e74c3c;
  margin-right: 1rem;
`;

const InfoContent = styled.div`
  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #666;
    line-height: 1.6;
  }
`;

const TestimonialsSection = styled(Section)`
  background-color: #f9f9f9;
  text-align: center;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const TestimonialCard = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  text-align: left;
`;

const TestimonialText = styled.p`
  font-style: italic;
  color: #666;
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #eee;
  margin-right: 1rem;
`;

const AuthorInfo = styled.div`
  h4 {
    margin: 0;
    font-size: 1.1rem;
  }
  
  p {
    margin: 0;
    color: #666;
    font-size: 0.9rem;
  }
`;

const Stars = styled.div`
  color: #f1c40f;
  font-size: 1rem;
  margin-top: 0.5rem;
`;

const ContactSection = styled(Section)`
  background-color: #2c3e50;
  color: white;
  text-align: center;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ContactCard = styled.div`
  padding: 2rem;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ContactIcon = styled.div`
  font-size: 2.5rem;
  color: #e74c3c;
  margin-bottom: 1.5rem;
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ContactText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
`;

const Footer = styled.footer`
  background-color: #1a252f;
  color: white;
  padding: 2rem;
  text-align: center;
  
  p {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const RestaurantDemoPage: React.FC = () => {
  return (
    <>
      <SEO 
        title="Demo de Sitio Web para Restaurantes | Web a Bajo Coste"
        description="Visualiza cómo quedaría tu restaurante online con este ejemplo interactivo. Diseño optimizado para atraer clientes y aumentar reservas."
        keywords="demo web restaurante, diseño web restaurantes, ejemplo web restaurante"
        canonical="https://webabajocoste.es/plantillas/restaurante"
      >
        <meta name="robots" content="noindex, nofollow" />
      </SEO>
      
      <DemoViewer
        title="Demo Web para Restaurantes"
        subtitle="Visualiza cómo podría ser tu nueva web de restaurante, diseñada para atraer nuevos clientes y mejorar tu presencia online"
        domainName="https://lapiazzaristorante.es"
        serviceLink="/servicios/diseno-web-restaurante"
        serviceName="Restaurantes"
      >
        <RestaurantDemo>
          <HeroSection>
            <HeroContent>
              <Logo>La Piazza</Logo>
              <HeroTitle>Auténtica Cocina Italiana</HeroTitle>
              <HeroSubtitle>Una experiencia gastronómica inigualable con los mejores sabores de Italia</HeroSubtitle>
              <ReservationButton>Reservar Mesa</ReservationButton>
            </HeroContent>
          </HeroSection>
          
          <MenuSection>
            <SectionTitle>Nuestro Menú</SectionTitle>
            <MenuCategories>
              <CategoryButton active>Entrantes</CategoryButton>
              <CategoryButton>Pastas</CategoryButton>
              <CategoryButton>Pizzas</CategoryButton>
              <CategoryButton>Postres</CategoryButton>
            </MenuCategories>
            
            <MenuGrid>
              <MenuItem>
                <MenuItemImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/restaurante-plato1.webp")' }} />
                <MenuItemContent>
                  <MenuItemTitle>
                    Bruschetta Clásica
                    <MenuItemPrice>8,90€</MenuItemPrice>
                  </MenuItemTitle>
                  <MenuItemDescription>
                    Pan tostado con tomate, albahaca, ajo y aceite de oliva virgen extra.
                  </MenuItemDescription>
                </MenuItemContent>
              </MenuItem>
              
              <MenuItem>
                <MenuItemImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/restaurante-plato2.webp")' }} />
                <MenuItemContent>
                  <MenuItemTitle>
                    Carpaccio di Manzo
                    <MenuItemPrice>12,50€</MenuItemPrice>
                  </MenuItemTitle>
                  <MenuItemDescription>
                    Finas láminas de ternera con rúcula, parmesano y aceite de trufa.
                  </MenuItemDescription>
                </MenuItemContent>
              </MenuItem>
              
              <MenuItem>
                <MenuItemImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/restaurante-plato3.webp")' }} />
                <MenuItemContent>
                  <MenuItemTitle>
                    Burrata con Tomate
                    <MenuItemPrice>14,90€</MenuItemPrice>
                  </MenuItemTitle>
                  <MenuItemDescription>
                    Cremosa burrata servida con tomates cherry y reducción de balsámico.
                  </MenuItemDescription>
                </MenuItemContent>
              </MenuItem>
              
              <MenuItem>
                <MenuItemImage style={{ backgroundImage: 'url("/servicios/tipo-cliente/restaurante-plato4.webp")' }} />
                <MenuItemContent>
                  <MenuItemTitle>
                    Arancini Sicilianos
                    <MenuItemPrice>9,50€</MenuItemPrice>
                  </MenuItemTitle>
                  <MenuItemDescription>
                    Croquetas de risotto rellenas de queso mozzarella y ragú.
                  </MenuItemDescription>
                </MenuItemContent>
              </MenuItem>
            </MenuGrid>
          </MenuSection>
          
          <AboutSection>
            <AboutContent>
              <AboutTitle>Nuestra Historia</AboutTitle>
              <AboutText>
                En La Piazza, llevamos más de 20 años trayendo la auténtica cocina italiana a tu ciudad. Nuestro chef, Mario Rossi, nacido en Nápoles, aprendió las recetas tradicionales de su abuela y las ha adaptado con un toque contemporáneo.
              </AboutText>
              <AboutText>
                Todos nuestros ingredientes son de la más alta calidad, importados directamente de Italia o seleccionados de entre los mejores productores locales.
              </AboutText>
              
              <InfoSection>
                <InfoItem>
                  <InfoIcon>
                    <FaClock />
                  </InfoIcon>
                  <InfoContent>
                    <h3>Horario</h3>
                    <p>Lunes a Jueves: 13:00 - 16:00 | 20:00 - 23:30</p>
                    <p>Viernes a Domingo: 13:00 - 16:30 | 20:00 - 00:30</p>
                  </InfoContent>
                </InfoItem>
                
                <InfoItem>
                  <InfoIcon>
                    <FaPhone />
                  </InfoIcon>
                  <InfoContent>
                    <h3>Reservas</h3>
                    <p>Teléfono: 912 345 678</p>
                    <p>Email: reservas@lapiazza.es</p>
                  </InfoContent>
                </InfoItem>
              </InfoSection>
            </AboutContent>
            
            <AboutImage />
          </AboutSection>
          
          <TestimonialsSection>
            <SectionTitle>Lo que opinan nuestros clientes</SectionTitle>
            
            <TestimonialsGrid>
              <TestimonialCard>
                <TestimonialText>
                  "La mejor pizza que he probado fuera de Italia. Los ingredientes son frescos y de calidad excepcional. El servicio es amable y atento."
                </TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar style={{ backgroundImage: 'url("/servicios/tipo-cliente/cliente1.webp")' }} />
                  <AuthorInfo>
                    <h4>María González</h4>
                    <Stars>
                      <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                    </Stars>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
              
              <TestimonialCard>
                <TestimonialText>
                  "Excelente experiencia gastronómica. La pasta carbonara es increíble, con un punto perfecto y una salsa espectacular. Volveré seguro."
                </TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar style={{ backgroundImage: 'url("/servicios/tipo-cliente/cliente2.webp")' }} />
                  <AuthorInfo>
                    <h4>Carlos Martínez</h4>
                    <Stars>
                      <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                    </Stars>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
              
              <TestimonialCard>
                <TestimonialText>
                  "Ambiente acogedor y elegante. La comida está deliciosa y la carta de vinos es muy completa. Un lugar ideal para una cena romántica."
                </TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar style={{ backgroundImage: 'url("/servicios/tipo-cliente/cliente3.webp")' }} />
                  <AuthorInfo>
                    <h4>Ana López</h4>
                    <Stars>
                      <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                    </Stars>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            </TestimonialsGrid>
          </TestimonialsSection>
          
          <ContactSection>
            <SectionTitle>Ven a visitarnos</SectionTitle>
            
            <ContactGrid>
              <ContactCard>
                <ContactIcon>
                  <FaMapMarkerAlt />
                </ContactIcon>
                <ContactTitle>Ubicación</ContactTitle>
                <ContactText>
                  Calle Gran Vía, 23<br />
                  28013 Madrid
                </ContactText>
              </ContactCard>
              
              <ContactCard>
                <ContactIcon>
                  <FaPhone />
                </ContactIcon>
                <ContactTitle>Reservas</ContactTitle>
                <ContactText>
                  Teléfono: 912 345 678<br />
                  Email: reservas@lapiazza.es
                </ContactText>
              </ContactCard>
              
              <ContactCard>
                <ContactIcon>
                  <FaCalendarAlt />
                </ContactIcon>
                <ContactTitle>Eventos</ContactTitle>
                <ContactText>
                  Organizamos eventos privados,<br />
                  celebraciones y comidas de empresa
                </ContactText>
              </ContactCard>
            </ContactGrid>
          </ContactSection>
          
          <Footer>
            <p>© 2025 La Piazza Ristorante | Todos los derechos reservados</p>
          </Footer>
        </RestaurantDemo>
      </DemoViewer>
    </>
  );
};

export default RestaurantDemoPage; 